// indotPayTable.js - INDOT Pay Table Utility
// Handles INDOT's MRI-based pay factor system for asphalt pavement smoothness

export class IndotPayTable {
  constructor() {
    this.name = 'INDOT';
    this.description = 'Indiana DOT Smoothness Specification for Asphalt Pavement';
    
    // Hardcoded pay factor ranges based on INDOT specifications
    this.payRanges = [
      { minMri: 0, maxMri: 35, payFactor: 1.06 },
      { minMri: 35, maxMri: 40, payFactor: 1.05 },
      { minMri: 40, maxMri: 45, payFactor: 1.04 },
      { minMri: 45, maxMri: 50, payFactor: 1.03 },
      { minMri: 50, maxMri: 55, payFactor: 1.02 },
      { minMri: 55, maxMri: 60, payFactor: 1.01 },
      { minMri: 60, maxMri: 70, payFactor: 1.00 },
      { minMri: 70, maxMri: 75, payFactor: 0.99 },
      { minMri: 75, maxMri: 80, payFactor: 0.98 },
      { minMri: 80, maxMri: 85, payFactor: 0.96 },
      { minMri: 85, maxMri: 90, payFactor: 0.95 },
      // Special handling for MRI > 90 based on pavement type
      { 
        minMri: 90, 
        maxMri: null, // Open-ended for Type A
        payFactorTypeA: 0.95,
        payFactorTypeB: null,
        noteTypeA: 'For Type A, PFs will be 0.95 and the section shall be corrected to 70 or less.',
        requiresCorrectionTypeA: true,
        targetMriTypeA: 70
      },
      {
        minMri: 90,
        maxMri: 110,
        payFactorTypeA: null,
        payFactorTypeB: 0.95,
        noteTypeB: 'For Type B, PFs will be 0.95 and the section does not require correction.',
        requiresCorrectionTypeB: false
      },
      {
        minMri: 110,
        maxMri: null, // Open-ended for Type B
        payFactorTypeA: null,
        payFactorTypeB: 0.95,
        noteTypeB: 'For Type B, PFs will be 0.95 and the section shall be corrected to 90 or less.',
        requiresCorrectionTypeB: true,
        targetMriTypeB: 90
      }
    ];
  }

  /**
   * Get pay factor for given MRI and pavement type
   * @param {number} mri - Mean Roughness Index in inches per mile
   * @param {string} pavementType - 'A' or 'B' 
   * @returns {number} Pay factor (typically 0.95 to 1.06)
   */
  getPayFactor(mri, pavementType = 'A') {
    const details = this.getPayDetails(mri, pavementType);
    return details.payFactor;
  }

  /**
   * Get detailed pay information for given MRI and pavement type
   * @param {number} mri - Mean Roughness Index in inches per mile
   * @param {string} pavementType - 'A' or 'B'
   * @returns {object} Detailed pay information
   */
  getPayDetails(mri, pavementType = 'A') {
    if (typeof mri !== 'number' || mri < 0) {
      throw new Error('MRI must be a non-negative number');
    }

    pavementType = pavementType.toUpperCase();
    if (!['A', 'B'].includes(pavementType)) {
      throw new Error('Pavement type must be "A" or "B"');
    }

    // Handle the complex ranges above MRI 90
    if (mri >= 90) {
      return this._getHighMriPayDetails(mri, pavementType);
    }

    // Handle standard ranges (MRI < 90)
    for (const range of this.payRanges) {
      if (this._mriInRange(mri, range.minMri, range.maxMri)) {
        return {
          payFactor: range.payFactor,
          mriRange: this._formatRange(range.minMri, range.maxMri),
          pavementType: pavementType,
          requiresCorrection: false,
          targetMri: null,
          note: null,
          category: this._getPayCategory(range.payFactor)
        };
      }
    }

    // Fallback for very high MRI values
    return {
      payFactor: 0.95,
      mriRange: `over ${mri}`,
      pavementType: pavementType,
      requiresCorrection: true,
      targetMri: pavementType === 'A' ? 70 : 90,
      note: `Extreme MRI value - correction required to ${pavementType === 'A' ? 70 : 90} or less`,
      category: 'penalty'
    };
  }

  /**
   * Handle complex pay logic for MRI >= 90
   */
  _getHighMriPayDetails(mri, pavementType) {
    if (pavementType === 'A') {
      // Type A: All MRI >= 90 get 0.95 factor and require correction to 70
      return {
        payFactor: 0.95,
        mriRange: 'over 90',
        pavementType: 'A',
        requiresCorrection: true,
        targetMri: 70,
        note: 'For Type A, PFs will be 0.95 and the section shall be corrected to 70 or less.',
        category: 'penalty'
      };
    } else {
      // Type B: Different logic for 90-110 vs >110
      if (mri <= 110) {
        return {
          payFactor: 0.95,
          mriRange: 'over 90 to 110',
          pavementType: 'B',
          requiresCorrection: false,
          targetMri: null,
          note: 'For Type B, PFs will be 0.95 and the section does not require correction.',
          category: 'penalty'
        };
      } else {
        return {
          payFactor: 0.95,
          mriRange: 'over 110',
          pavementType: 'B',
          requiresCorrection: true,
          targetMri: 90,
          note: 'For Type B, PFs will be 0.95 and the section shall be corrected to 90 or less.',
          category: 'penalty'
        };
      }
    }
  }

  /**
   * Calculate complete payment for a lot/section
   * @param {object} lotData - Lot information including MRI, area, etc.
   * @param {object} projectConfig - Project configuration
   * @returns {object} Complete payment calculation
   */
  calculateLotPayment(lotData, projectConfig) {
    const {
      mri,
      lengthFt,
      startStation,
      endStation,
      lotNumber
    } = lotData;

    const {
      lane_width_ft = 12.0,
      spread_rate_lbs_sy = 330.0,
      unit_price_per_ton = 150.0,
      pavement_type = 'A',
      material_density_lbs_cy = 150.0
    } = projectConfig;

    // Get pay factor details
    const payDetails = this.getPayDetails(mri, pavement_type);

    // Calculate tonnage
    const areaFt2 = lengthFt * lane_width_ft;
    const areaSY = areaFt2 / 9; // Convert to square yards
    const weightLbs = areaSY * spread_rate_lbs_sy;
    const tonnage = weightLbs / 2000; // Convert to tons

    // Calculate payments
    const basePayment = tonnage * unit_price_per_ton;
    const adjustedPayment = basePayment * payDetails.payFactor;
    const dollarAdjustment = adjustedPayment - basePayment;

    return {
      // Lot identification
      lotNumber: lotNumber,
      startStation: startStation,
      endStation: endStation,
      lengthFt: lengthFt,
      lengthMiles: lengthFt / 5280,

      // Quality metrics
      mri: mri,
      pavementType: pavement_type,
      mriRange: payDetails.mriRange,
      qualityCategory: this._getQualityCategory(mri),

      // Pay calculations
      payFactor: payDetails.payFactor,
      tonnage: tonnage,
      basePayment: basePayment,
      adjustedPayment: adjustedPayment,
      dollarAdjustment: dollarAdjustment,
      percentAdjustment: ((payDetails.payFactor - 1.0) * 100),

      // Special requirements
      requiresCorrection: payDetails.requiresCorrection,
      targetMri: payDetails.targetMri,
      note: payDetails.note,

      // Categorization
      payCategory: payDetails.category,
      exceedsThreshold: this._exceedsThreshold(mri, pavement_type, projectConfig),

      // Additional calculations
      areaSquareYards: areaSY,
      unitPrice: unit_price_per_ton
    };
  }

  /**
   * Calculate summary statistics for multiple lots
   * @param {array} lotResults - Array of lot payment results
   * @returns {object} Summary statistics
   */
  calculateProjectSummary(lotResults) {
    if (!lotResults || lotResults.length === 0) {
      return this._getEmptySummary();
    }

    const totalLots = lotResults.length;
    const totalLengthFt = lotResults.reduce((sum, lot) => sum + lot.lengthFt, 0);
    const totalTonnage = lotResults.reduce((sum, lot) => sum + lot.tonnage, 0);
    const totalBasePayment = lotResults.reduce((sum, lot) => sum + lot.basePayment, 0);
    const totalAdjustedPayment = lotResults.reduce((sum, lot) => sum + lot.adjustedPayment, 0);
    const totalDollarAdjustment = lotResults.reduce((sum, lot) => sum + lot.dollarAdjustment, 0);

    // Calculate MRI statistics
    const mriValues = lotResults.map(lot => lot.mri);
    const avgMri = mriValues.reduce((sum, mri) => sum + mri, 0) / mriValues.length;
    const minMri = Math.min(...mriValues);
    const maxMri = Math.max(...mriValues);

    // Pay factor statistics
    const payFactors = lotResults.map(lot => lot.payFactor);
    const avgPayFactor = payFactors.reduce((sum, pf) => sum + pf, 0) / payFactors.length;
    const minPayFactor = Math.min(...payFactors);
    const maxPayFactor = Math.max(...payFactors);

    // Categorize lots
    const bonusLots = lotResults.filter(lot => lot.payFactor > 1.0).length;
    const neutralLots = lotResults.filter(lot => lot.payFactor === 1.0).length;
    const penaltyLots = lotResults.filter(lot => lot.payFactor < 1.0).length;
    const correctionRequiredLots = lotResults.filter(lot => lot.requiresCorrection).length;
    const thresholdExceededLots = lotResults.filter(lot => lot.exceedsThreshold).length;

    return {
      totalLots,
      totalLengthFt,
      totalLengthMiles: totalLengthFt / 5280,
      totalTonnage,
      totalBasePayment,
      totalAdjustedPayment,
      totalDollarAdjustment,
      percentAdjustment: totalBasePayment > 0 ? (totalDollarAdjustment / totalBasePayment) * 100 : 0,

      // MRI statistics
      avgMri,
      minMri,
      maxMri,
      mriStandardDeviation: this._calculateStandardDeviation(mriValues),

      // Pay factor statistics
      avgPayFactor,
      minPayFactor,
      maxPayFactor,

      // Lot categorization
      bonusLots,
      neutralLots,
      penaltyLots,
      correctionRequiredLots,
      thresholdExceededLots,

      // Percentages
      bonusPercentage: (bonusLots / totalLots) * 100,
      neutralPercentage: (neutralLots / totalLots) * 100,
      penaltyPercentage: (penaltyLots / totalLots) * 100,
      correctionPercentage: (correctionRequiredLots / totalLots) * 100,
      thresholdExceedancePercentage: (thresholdExceededLots / totalLots) * 100,

      // Overall assessment
      overallQuality: this._getQualityCategory(avgMri),
      projectCompliance: this._assessProjectCompliance(lotResults)
    };
  }

  /**
   * Validate project configuration for INDOT calculations
   * @param {object} config - Project configuration
   * @returns {object} Validation result
   */
  validateConfiguration(config) {
    const errors = [];
    const warnings = [];

    // Required fields
    const required = [
      { field: 'lane_width_ft', name: 'Lane Width' },
      { field: 'spread_rate_lbs_sy', name: 'Spread Rate' },
      { field: 'unit_price_per_ton', name: 'Unit Price' }
    ];

    required.forEach(({ field, name }) => {
      if (!config[field] || config[field] <= 0) {
        errors.push(`${name} is required and must be greater than zero`);
      }
    });

    // Validate pavement type
    if (config.pavement_type && !['A', 'B'].includes(config.pavement_type.toUpperCase())) {
      errors.push('Pavement type must be "A" or "B"');
    }

    // Check for reasonable values
    if (config.lane_width_ft && (config.lane_width_ft < 8 || config.lane_width_ft > 16)) {
      warnings.push('Lane width seems unusual (typically 10-14 feet)');
    }

    if (config.spread_rate_lbs_sy && (config.spread_rate_lbs_sy < 100 || config.spread_rate_lbs_sy > 600)) {
      warnings.push('Spread rate seems unusual (typically 200-400 lbs/sy)');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  // Helper methods
  _mriInRange(mri, min, max) {
    if (max === null) return mri > min;
    return mri > min && mri <= max;
  }

  _formatRange(min, max) {
    if (max === null) return `over ${min}`;
    return `over ${min} to ${max}`;
  }

  _getPayCategory(payFactor) {
    if (payFactor > 1.0) return 'bonus';
    if (payFactor < 1.0) return 'penalty';
    return 'neutral';
  }

  _getQualityCategory(mri) {
    if (mri <= 35) return 'excellent';
    if (mri <= 60) return 'good';
    if (mri <= 85) return 'fair';
    if (mri <= 110) return 'poor';
    return 'very_poor';
  }

  _exceedsThreshold(mri, pavementType, config) {
    const thresholdField = pavementType === 'A' ? 'iri_threshold_type_a' : 'iri_threshold_type_b';
    const threshold = config[thresholdField] || (pavementType === 'A' ? 160 : 170);
    return mri > threshold;
  }

  _calculateStandardDeviation(values) {
    if (values.length <= 1) return 0;
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / (values.length - 1);
    return Math.sqrt(variance);
  }

  _getEmptySummary() {
    return {
      totalLots: 0,
      totalLengthFt: 0,
      totalLengthMiles: 0,
      totalTonnage: 0,
      totalBasePayment: 0,
      totalAdjustedPayment: 0,
      totalDollarAdjustment: 0,
      percentAdjustment: 0,
      avgMri: 0,
      minMri: 0,
      maxMri: 0,
      mriStandardDeviation: 0,
      avgPayFactor: 1.0,
      minPayFactor: 1.0,
      maxPayFactor: 1.0,
      bonusLots: 0,
      neutralLots: 0,
      penaltyLots: 0,
      correctionRequiredLots: 0,
      thresholdExceededLots: 0,
      bonusPercentage: 0,
      neutralPercentage: 0,
      penaltyPercentage: 0,
      correctionPercentage: 0,
      thresholdExceedancePercentage: 0,
      overallQuality: 'unknown',
      projectCompliance: 'unknown'
    };
  }

  _assessProjectCompliance(lotResults) {
    const correctionRequired = lotResults.some(lot => lot.requiresCorrection);
    const avgPayFactor = lotResults.reduce((sum, lot) => sum + lot.payFactor, 0) / lotResults.length;
    
    if (correctionRequired) return 'non_compliant';
    if (avgPayFactor >= 1.02) return 'excellent';
    if (avgPayFactor >= 1.0) return 'good';
    if (avgPayFactor >= 0.98) return 'acceptable';
    return 'poor';
  }
}

// Export a singleton instance for easy use
export const indotPayTable = new IndotPayTable();